import React from 'react';
import { 
  Box, 
  Container,
  Grid,
  Stack, 
  styled, 
  Typography,
  IconButton,
  Divider,
} from '@mui/material';
import { Link } from 'react-router-dom';
import FacebookIcon from '@mui/icons-material/Facebook';
import InstagramIcon from '@mui/icons-material/Instagram';
import PhoneIcon from '@mui/icons-material/Phone';
import EmailIcon from '@mui/icons-material/Email';
import LocationOnIcon from '@mui/icons-material/LocationOn';

const Footer = () => {
  const FooterWrapper = styled(Box)(({ theme }) => ({
    background: 'linear-gradient(135deg, #1a1a2e 0%, #16213e 100%)',
    color: '#fff',
    paddingTop: theme.spacing(8),
    paddingBottom: theme.spacing(4),
  }));

  const FooterTitle = styled(Typography)(({ theme }) => ({
    fontFamily: "'Outfit', 'Roboto', sans-serif",
    fontWeight: 700,
    fontSize: '1.25rem',
    marginBottom: theme.spacing(3),
    color: '#fff',
    position: 'relative',
    paddingBottom: theme.spacing(1),
    '&::after': {
      content: '""',
      position: 'absolute',
      bottom: 0,
      left: 0,
      width: '50px',
      height: '3px',
      background: 'linear-gradient(90deg, #FF6B35 0%, #FF8C42 100%)',
      borderRadius: '2px',
    },
  }));

  const FooterLink = styled(Typography)(({ theme }) => ({
    color: 'rgba(255, 255, 255, 0.7)',
    fontSize: '0.95rem',
    marginBottom: theme.spacing(1.5),
    cursor: 'pointer',
    transition: 'all 0.3s ease',
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
    '&:hover': {
      color: '#FF6B35',
      transform: 'translateX(5px)',
    },
    '& svg': {
      fontSize: '1.2rem',
    },
  }));

  const SocialButton = styled(IconButton)(({ theme }) => ({
    color: 'rgba(255, 255, 255, 0.7)',
    border: '2px solid rgba(255, 255, 255, 0.2)',
    transition: 'all 0.3s ease',
    '&:hover': {
      color: '#FF6B35',
      borderColor: '#FF6B35',
      background: 'rgba(255, 107, 53, 0.1)',
      transform: 'translateY(-3px)',
    },
  }));

  const BrandSection = styled(Box)(({ theme }) => ({
    marginBottom: theme.spacing(4),
  }));

  const BrandName = styled(Typography)(({ theme }) => ({
    fontFamily: "'Outfit', 'Roboto', sans-serif",
    fontWeight: 800,
    fontSize: '1.8rem',
    marginBottom: theme.spacing(2),
    background: 'linear-gradient(135deg, #FF6B35 0%, #FF8C42 100%)',
    WebkitBackgroundClip: 'text',
    WebkitTextFillColor: 'transparent',
    backgroundClip: 'text',
  }));

  const BrandDescription = styled(Typography)({
    color: 'rgba(255, 255, 255, 0.7)',
    fontSize: '0.95rem',
    lineHeight: 1.8,
    maxWidth: '350px',
  });

  const Copyright = styled(Box)(({ theme }) => ({
    marginTop: theme.spacing(6),
    paddingTop: theme.spacing(4),
    borderTop: '1px solid rgba(255, 255, 255, 0.1)',
    textAlign: 'center',
  }));

  const quickLinks = [
    { text: 'Home', to: '/' },
    { text: 'About Us', to: '/about' },
    { text: 'Contact', to: '/contact' },
  ];

  const industries = [
    'Construction',
    'Infrastructure Development',
    'Renewable Energy',
    'Industrial Projects',
    'Shipbuilding',
  ];

  return (
    <FooterWrapper component="footer">
      <Container maxWidth="lg">
        <Grid container spacing={4}>
          {/* Brand Section */}
          <Grid item xs={12} md={4}>
            <BrandSection>
              <BrandName>
                Perumal Crane Service
              </BrandName>
              <BrandDescription>
                Leading crane rental service in Coimbatore, providing professional heavy lifting 
                solutions with capacities from 10 to 200 tons. Your trusted partner for safe and 
                efficient crane operations.
              </BrandDescription>
              <Stack direction="row" spacing={2} sx={{ mt: 3 }}>
                <SocialButton 
                  component="a"
                  href="https://www.instagram.com/perumal_crane_services_1/"
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label="Instagram"
                >
                  <InstagramIcon />
                </SocialButton>
                <SocialButton 
                  component="a"
                  href="https://www.facebook.com/p/Sri-Perumal-Crane-Services-100069455018192/?locale=en"
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label="Facebook"
                >
                  <FacebookIcon />
                </SocialButton>
              </Stack>
            </BrandSection>
          </Grid>

          {/* Quick Links */}
          <Grid item xs={12} sm={6} md={2}>
            <FooterTitle>Quick Links</FooterTitle>
            <Stack>
              {quickLinks.map((link, index) => (
                <FooterLink 
                  key={index}
                  component={Link}
                  to={link.to}
                >
                  {link.text}
                </FooterLink>
              ))}
            </Stack>
          </Grid>

          {/* Industries Served */}
          <Grid item xs={12} sm={6} md={3}>
            <FooterTitle>Industries Served</FooterTitle>
            <Stack>
              {industries.map((industry, index) => (
                <FooterLink key={index}>
                  {industry}
                </FooterLink>
              ))}
            </Stack>
          </Grid>

          {/* Contact Info */}
          <Grid item xs={12} md={3}>
            <FooterTitle>Contact Us</FooterTitle>
            <Stack>
              <FooterLink 
                component="a"
                href="tel:+917639568608"
              >
                <PhoneIcon />
                +91 76395 68608
              </FooterLink>
              <FooterLink 
                component="a"
                href="mailto:perumalcraneservices@gmail.com"
              >
                <EmailIcon />
                perumalcraneservices@gmail.com
              </FooterLink>
              <FooterLink>
                <LocationOnIcon />
                Palladam - Cochin Frontier Road, Chettipalayam, Tamil Nadu 641201
              </FooterLink>
            </Stack>
          </Grid>
        </Grid>

        <Copyright>
          <Typography 
            variant="body2" 
            sx={{ 
              color: 'rgba(255, 255, 255, 0.6)',
              fontSize: '0.9rem',
            }}
          >
            © {new Date().getFullYear()} Perumal Crane Service. All rights reserved.
          </Typography>
          <Typography 
            variant="caption" 
            sx={{ 
              color: 'rgba(255, 255, 255, 0.4)',
              fontSize: '0.8rem',
              mt: 1,
              display: 'block',
            }}
          >
            Professional Crane Rental Services in Coimbatore | 24/7 Support Available
          </Typography>
        </Copyright>
      </Container>
    </FooterWrapper>
  );
};

export default Footer;